import React, { useCallback, useEffect, useState } from 'react'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  mobile,
} from '../components/mobile'
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CalendarIcon,
  Loader2Icon,
  ClipboardListIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { markContractQcCompleted } from '../lib/qcRotationQueue'
import { fetchQcInspectionsList } from '../lib/qcInspectionsQuery'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useDivision } from '../contexts/DivisionContext'

interface QcInspectionRow {
  id: string
  scheduled_date?: string | null
  inspection_date?: string | null
  status: string
  priority?: string | null
  overall_rating?: number | null
  findings?: string | null
  requires_followup?: boolean | null
  followup_date?: string | null
  contract_id?: string | null
  job_id?: string | null
  inspector_id?: string | null
  contract?: { contract_number?: string | null; title?: string | null } | null
  job?: { title?: string | null; job_number?: string | null } | null
  inspector?: { first_name?: string; last_name?: string } | null
}

export function QCVisits() {
  const { currentDivision } = useDivision()
  const [scheduled, setScheduled] = useState<QcInspectionRow[]>([])
  const [completed, setCompleted] = useState<QcInspectionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [recordingId, setRecordingId] = useState<string | null>(null)
  const [result, setResult] = useState({
    outcome: 'passed',
    overallRating: 4,
    findings: '',
    category: 'hallway',
    requiresFollowup: false,
    followupDate: '',
  })
  const [submitting, setSubmitting] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [scheduledRes, completedRes] = await Promise.all([
        fetchQcInspectionsList({
          statusEq: 'scheduled',
          orderBy: 'scheduled_date',
          ascending: true,
          division: currentDivision,
        }),
        fetchQcInspectionsList({
          statusIn: ['passed', 'failed'],
          orderBy: 'inspection_date',
          ascending: false,
          limit: 50,
          division: currentDivision,
        }),
      ])

      if (scheduledRes.error) throw scheduledRes.error
      if (completedRes.error) throw completedRes.error

      setScheduled((scheduledRes.data || []) as QcInspectionRow[])
      setCompleted((completedRes.data || []) as QcInspectionRow[])
    } catch (err: any) {
      console.error('Error fetching QC visits:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [currentDivision])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openRecordResult = (inspection: QcInspectionRow) => {
    setRecordingId(inspection.id)
    setResult({
      outcome: 'passed',
      overallRating: 4,
      findings: '',
      category: 'hallway',
      requiresFollowup: false,
      followupDate: '',
    })
  }

  const submitResult = async (inspection: QcInspectionRow) => {
    try {
      setSubmitting(true)
      const newStatus = result.outcome === 'passed' ? 'passed' : 'failed'

      const { error: updateError } = await supabase
        .from('qc_inspections')
        .update({
          status: newStatus,
          inspection_date: new Date().toISOString(),
          overall_rating: result.overallRating,
          findings: result.findings || null,
          requires_followup: result.requiresFollowup,
          followup_date: result.requiresFollowup ? result.followupDate || null : null,
        })
        .eq('id', inspection.id)
      if (updateError) throw updateError

      let findingId: string | null = null
      if (result.findings.trim()) {
        const { data: insertedFinding } = await supabase
          .from('qc_findings')
          .insert({
            qc_inspection_id: inspection.id,
            description: result.findings.trim(),
            severity: newStatus === 'failed' ? 'major' : 'minor',
            category: result.category || null,
            status: 'open',
          })
          .select('id')
          .single()
        findingId = insertedFinding?.id || null
      }

      if (inspection.contract_id) {
        await markContractQcCompleted(inspection.contract_id)
        if (newStatus === 'failed' && findingId && result.findings.trim()) {
          const { carryFindingsToNextVisit } = await import('../lib/qcFindingCarryForward')
          await carryFindingsToNextVisit({
            contractId: inspection.contract_id,
            findings: [
              {
                id: findingId,
                description: result.findings.trim(),
                category: result.category,
              },
            ],
          })
        }
      }

      if (inspection.job_id) {
        await supabase
          .from('jobs')
          .update({ status: newStatus === 'passed' ? 'qc_passed' : 'qc_failed' })
          .eq('id', inspection.job_id)
      }

      setRecordingId(null)
      await fetchData()
    } catch (err: any) {
      console.error('Error recording QC result:', err)
      alert('Failed to record QC result: ' + (err.message || 'Unknown error'))
    } finally {
      setSubmitting(false)
    }
  }

  const QC_VISITS_COLUMNS = [
    { id: 'subject', label: 'Contract / Job' },
    { id: 'inspector', label: 'Inspector' },
    { id: 'date', label: 'Date' },
    { id: 'rating', label: 'Rating' },
    { id: 'status', label: 'Status' },
    { id: 'findings', label: 'Findings' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(QC_VISITS_COLUMNS, { storageKey: 'qc_visits' })

  const subjectLabel = (v: QcInspectionRow) =>
    v.contract?.title || v.job?.title || 'QC Visit'
  const subjectSub = (v: QcInspectionRow) =>
    v.contract?.contract_number ? `#${v.contract.contract_number}` : v.job?.job_number ? `#${v.job.job_number}` : ''

  if (loading) {
    return (
      <div className="space-y-6 px-4 flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <MobilePage>
      <div>
        <MobilePageHeader title="QC Visits" />
        <p className="mt-1 text-gray-600">Quality control visit tracking and results</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{scheduled.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <ClockIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Passed</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{completed.filter((c) => c.status === 'passed').length}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{completed.filter((c) => c.status === 'failed').length}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 text-red-600">
              <XCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Needs Follow-up</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{completed.filter((c) => c.requires_followup).length}</p>
            </div>
            <div className="p-3 rounded-lg bg-orange-50 text-orange-600">
              <ClipboardListIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Scheduled visits */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
          <h2 className="text-lg font-semibold text-gray-900">Scheduled Visits ({scheduled.length})</h2>
        </div>
        {scheduled.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No QC visits scheduled.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scheduled.map((v) => (
              <div key={v.id} className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {subjectLabel(v)} <span className="text-xs text-gray-400">{subjectSub(v)}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {v.scheduled_date ? new Date(v.scheduled_date + 'T00:00:00').toLocaleDateString() : '—'}
                      {v.inspector ? ` · ${v.inspector.first_name || ''} ${v.inspector.last_name || ''}`.trim() : ' · Unassigned'}
                    </p>
                  </div>
                  {recordingId !== v.id && (
                    <button
                      onClick={() => openRecordResult(v)}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Record Result
                    </button>
                  )}
                </div>

                {recordingId === v.id && (
                  <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                    <div className="flex gap-2">
                      <select
                        value={result.outcome}
                        onChange={(e) => setResult({ ...result, outcome: e.target.value })}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        <option value="passed">Passed</option>
                        <option value="failed">Failed</option>
                      </select>
                      <select
                        value={result.overallRating}
                        onChange={(e) => setResult({ ...result, overallRating: Number(e.target.value) })}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {n} star{n > 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <textarea
                      value={result.findings}
                      onChange={(e) => setResult({ ...result, findings: e.target.value })}
                      placeholder="Findings / notes…"
                      rows={2}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    {result.outcome === 'failed' && (
                      <select
                        value={result.category}
                        onChange={(e) => setResult({ ...result, category: e.target.value })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        title="Category for next-visit follow-up"
                      >
                        <option value="hallway">Hallway</option>
                        <option value="garbage">Garbage</option>
                        <option value="exterior">Exterior</option>
                        <option value="general">General</option>
                      </select>
                    )}
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={result.requiresFollowup}
                        onChange={(e) => setResult({ ...result, requiresFollowup: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      Requires follow-up
                    </label>
                    {result.requiresFollowup && (
                      <input
                        type="date"
                        value={result.followupDate}
                        onChange={(e) => setResult({ ...result, followupDate: e.target.value })}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitResult(v)}
                        disabled={submitting}
                        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {submitting ? 'Saving…' : 'Save Result'}
                      </button>
                      <button
                        onClick={() => setRecordingId(null)}
                        className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completed visits */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Completed Visits</h2>
          <ColumnSelector columns={QC_VISITS_COLUMNS} visibleColumns={visibleColumns} onToggle={toggleColumn} />
        </div>
        {completed.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No completed visits found.</div>
        ) : (
          <>
      <p className={mobile.sectionLabel}>Completed</p>
      <div className="native-mobile-list space-y-2">
        {completed.length === 0 ? (
          <MobileEmptyState title="No completed visits" />
        ) : (
          completed.map((v) => (
            <MobileListRow
              key={v.id}
              title={subjectLabel(v)}
              subtitle={subjectSub(v)}
              badges={
                <span className={`${mobile.badge} ${
                  v.status === "passed"
                    ? "bg-green-50 text-green-700"
                    : v.status === "failed"
                      ? "bg-red-50 text-red-700"
                      : "bg-gray-100 text-gray-700"
                }`}>{v.status}</span>
              }
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('subject') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract / Job</th>}
                  {visibleColumns.has('inspector') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inspector</th>}
                  {visibleColumns.has('date') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>}
                  {visibleColumns.has('rating') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>}
                  {visibleColumns.has('status') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
                  {visibleColumns.has('findings') && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {completed.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    {visibleColumns.has('subject') && (
                      <td className="px-6 py-4 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{subjectLabel(v)}</div>
                        <div className="text-xs text-gray-500">{subjectSub(v)}</div>
                      </td>
                    )}
                    {visibleColumns.has('inspector') && (
                      <td className="px-6 py-4 min-w-0 text-sm text-gray-900">
                        {v.inspector ? `${v.inspector.first_name || ''} ${v.inspector.last_name || ''}`.trim() : '—'}
                      </td>
                    )}
                    {visibleColumns.has('date') && (
                      <td className="px-6 py-4 min-w-0 text-sm text-gray-500">
                        {v.inspection_date ? new Date(v.inspection_date).toLocaleDateString() : '—'}
                      </td>
                    )}
                    {visibleColumns.has('rating') && (
                      <td className="px-6 py-4 min-w-0 text-sm text-gray-900">
                        {v.overall_rating ? `${v.overall_rating}/5` : '—'}
                      </td>
                    )}
                    {visibleColumns.has('status') && (
                      <td className="px-6 py-4 min-w-0">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            v.status === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {v.status === 'passed' ? 'Passed' : 'Failed'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('findings') && (
                      <td className="px-6 py-4 text-sm min-w-0 text-gray-500 max-w-xs truncate">
                        {v.findings || '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-400">
        <CalendarIcon className="w-3.5 h-3.5" />
        Rotation queue and rush requests are managed on the QC Schedule page.
      </div>
    </MobilePage>
  )
}